/**
 * Builds the collision representation of a level.
 *
 * Split out of World.ts on purpose: this has no three.js dependency, so tests
 * (and, later, a server-side run validator) can load the real level and simulate
 * it in plain node.
 */

import { DOOR } from '../config/gameplay';
import { BOOT_TAG, type Collider, type CollisionWorld } from './CollisionWorld';
import {
  BOOT_SUFFIX,
  campTentBox,
  doorLeafBoxes,
  sleeperBox,
  solidColliders,
  type LevelDefinition,
} from './Level';

export interface LevelColliders {
  /**
   * Door id -> the pieces of its leaf, which `Door` moves as the thing swings.
   *
   * A list rather than a box, because the leaf is a solid that turns and this
   * world only knows boxes: see `doorLeafBoxes`, which is also what cuts them.
   */
  doors: Map<string, Collider[]>;
  /** Solid name -> its first collider, for the few things that switch on and off. */
  solids: Map<string, Collider>;
  /** The glass in the pirtti's window, until somebody punches it out. */
  ikkuna: Collider;
}

export function populateCollisionWorld(
  level: LevelDefinition,
  collision: CollisionWorld,
): LevelColliders {
  const solids = new Map<string, Collider>();
  for (const spec of level.solids) {
    // One box for almost everything, two for a car with a boot lid. The split
    // lives in `solidColliders` so the physics, the fingerprint and the F4
    // overlay cannot disagree about what is solid.
    //
    // The lid gets a tag of its own because it is the one surface in the venue
    // a jump is meant to end on: `stepPlayer` gives the take-off price back to
    // anybody who lands on one. It is read off `spec.boot`, which splits the
    // collider and is hashed, and deliberately not off `spec.shape`, which is
    // paint - see `src/config/CLAUDE.md`. A car may still be given wheels for
    // free; what earns the refund is having a tail you can get on top of.
    const lid = spec.boot ? `${spec.name}${BOOT_SUFFIX}` : null;
    for (const [id, box] of solidColliders(spec)) {
      const collider = collision.add(id, box, id === lid ? BOOT_TAG : 'level');
      if (!solids.has(id)) solids.set(id, collider);
    }
  }
  for (const spec of level.sleepers) {
    collision.add(spec.name, sleeperBox(spec), 'sleeper');
  }
  for (const spec of level.campTents) {
    collision.add(spec.name, campTentBox(spec), 'level');
  }
  // The leaves, shut. Each is `DOOR.segments` boxes rather than one, because a
  // door swings and this world is axis-aligned - `Door` re-cuts them every
  // step the angle moves. They are built at angle zero so that a level nobody
  // ever presses F in is a level with three shut doors in it.
  const doors = new Map<string, Collider[]>();
  for (const spec of level.doors) {
    doors.set(
      spec.id,
      doorLeafBoxes(spec, 0, DOOR.segments).map((box, index) =>
        collision.add(`${spec.id}-${index + 1}`, box, 'door'),
      ),
    );
  }
  // The pane. It is not in `level.solids` because it is not a solid: it is a
  // thing that stops being one, and `Ikkuna` owns the step it does. Its box is
  // hashed all the same - `simulationFingerprint` has taken `ikkuna.pane`
  // since the glass first cost time, and the collider is that same box.
  const ikkuna = collision.add('pirtin-ikkunalasi', level.ikkuna.pane, 'glass');
  return { doors, solids, ikkuna };
}
